import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useOnboardingFooter } from '../../components/OnboardingFooterContext';
import { BriefcaseIcon, ConnectedAccountsIcon, CreditCardIcon, IndustryIcon, UserIcon } from '../../assets/icons';
import { UserType } from '../../enums';
import { getResolvedNextStepPath, getResolvedPreviousStepPath, isKycOrLaterOnboardingStep, scrollOnboardingToTop } from '../../utils/onboardingUtils';
import normalizeReferenceCountries from '../../utils/referenceCountries';
import * as configActions from '../../store/actions/config';
import * as userActions from '../../store/actions/user';
import {
  buildUserOnboardingProfilePayload,
  isFinancialFirstScreenComplete,
  isFinancialSecondScreenComplete,
  normalizeMultiValue,
} from '../../utils/financialOnboardingStorage';
import { requiresEnhancedIndividualKyc } from '../../utils/individualEnhancedKyc';
import SearchableSelect from '../../components/SearchableSelect';
import FlagIcon from '../../components/FlagIcon';
import CompanyActivity from '../CompanyActivity';
import * as API from '../../store/sagas/api';

const ACCOUNT_USE_OPTIONS = [
  { value: 'PersonalPayments', title: 'Personal payments', description: 'Transfers and payments for personal use', Icon: UserIcon },
  { value: 'BusinessPayments', title: 'Business payments', description: 'Supplier, employee or client payments', Icon: BriefcaseIcon },
  { value: 'PersonalAndBusinessPayments', title: 'Both', description: 'Personal and business use', Icon: ConnectedAccountsIcon },
];

const PAYMENT_TYPE_OPTIONS = [
  { value: 'EverydayPurchases', label: 'Everyday purchases' },
  { value: 'TransfersFamilyFriends', label: 'Transfers to family or friends' },
  { value: 'TransfersBetweenAccounts', label: 'Transfers between my own accounts' },
  { value: 'PaymentsToSuppliersOrEmployees', label: 'Payments to suppliers or employees' },
  { value: 'InternationalTransfers', label: 'International transfers' },
  { value: 'Other', label: 'Other' },
];

const MONTHLY_VOLUME_OPTIONS = [
  { value: 'Under1000', label: 'Under 1.000 €' },
  { value: 'From1000To10000', label: '1.000 € - 10.000 €' },
  { value: 'From10000To50000', label: '10.000 € - 50.000 €' },
  { value: 'Over50000', label: 'Over 50.000 €' },
];

const SOURCE_INCOME_OPTIONS = [
  { value: 'Salary', label: 'Salary' },
  { value: 'SelfEmployment', label: 'Self-employment' },
  { value: 'BusinessOwnership', label: 'Business ownership' },
  { value: 'Investments', label: 'Investments' },
  { value: 'Pension', label: 'Pension' },
  { value: 'StudentSupport', label: 'Student support' },
  { value: 'FamilySupport', label: 'Family support' },
  { value: 'Savings', label: 'Savings' },
  { value: 'Other', label: 'Other' },
];

const EMPLOYMENT_STATUS_OPTIONS = [
  { value: 'Employed', label: 'Employed' },
  { value: 'SelfEmployed', label: 'Self-employed' },
  { value: 'BusinessOwner', label: 'Business owner' },
  { value: 'Unemployed', label: 'Unemployed' },
  { value: 'Student', label: 'Student' },
  { value: 'Retired', label: 'Retired' },
  { value: 'Homemaker', label: 'Homemaker' },
  { value: 'Other', label: 'Other' },
];

const INDUSTRY_REQUIRED_STATUSES = ['Employed', 'SelfEmployed', 'BusinessOwner'];

const INDUSTRY_OPTIONS = [
  { value: 'EducationAndMedicine', label: 'Education and Medicine', icon: 'healthcare' },
  { value: 'ArchitecturalAndEngineering', label: 'Architectural and engineering', icon: 'construction' },
  { value: 'SocialWorkAndArts', label: 'Social work and Arts', icon: 'consulting' },
  { value: 'RealEstateAndConstruction', label: 'Real Estate & Construction', icon: 'realEstate' },
  { value: 'RetailAndECommerce', label: 'Retail & E-commerce', icon: 'retail' },
  { value: 'LogisticsAndTransport', label: 'Logistics & Transport', icon: 'logistics' },
  { value: 'TechnologyAndSoftware', label: 'Technology & Software', icon: 'technology' },
  { value: 'FinanceAndBanking', label: 'Finance & Banking', icon: 'finance' },
  { value: 'ConsultingAndAdvisory', label: 'Consulting & Advisory', icon: 'consulting' },
  { value: 'Manufacturing', label: 'Manufacturing', icon: 'manufacturing' },
  { value: 'AgricultureAndFood', label: 'Agriculture & Food', icon: 'agriculture' },
  { value: 'CryptoGambling', label: 'Crypto, gambling', icon: 'finance' },
  { value: 'DefenseArmour', label: 'Defense, armour', icon: 'other' },
  { value: 'Charity', label: 'Charity', icon: 'consulting' },
  { value: 'SaleOfLuxuryGoods', label: 'Sale of luxury goods', icon: 'retail' },
  { value: 'HealthcareAndPharmaceuticals', label: 'Healthcare & Pharmaceuticals', icon: 'healthcare' },
  { value: 'Other', label: 'Other', icon: 'other' },
];

const ESTIMATED_INCOME_OPTIONS = [
  { value: 'Under15000', label: 'Under 15.000 €' },
  { value: 'From15000To30000', label: '15.000 € - 30.000 €' },
  { value: 'From30001To60000', label: '30.001 € - 60.000 €' },
  { value: 'From60001To100000', label: '60.001 € - 100.000 €' },
  { value: 'Over100000', label: 'Over 100.000 €' },
  { value: 'PreferNotToSay', label: 'Prefer not to say' },
];

const fieldLabelClass = 'mb-2 block text-[12px] font-normal text-[#3D5A73]';
const errorClass = 'mt-1.5 text-xs font-medium text-[#C0392B]';
const inputClass =
  'h-11 w-full rounded-[10px] border border-[#DDE6EF] bg-white px-3.5 text-sm text-[#0B1929] outline-none transition placeholder:text-[#7A9AB0]/70 focus:border-[#2E6BAD] focus:ring-4 focus:ring-[#2E6BAD]/10';
const searchableSelectTriggerClass =
  'flex h-11 w-full items-center gap-2 rounded-[10px] border border-[#DDE6EF] bg-white px-3.5 text-sm text-[#0B1929] outline-none transition focus:border-[#2E6BAD] focus:ring-4 focus:ring-[#2E6BAD]/10 disabled:cursor-not-allowed disabled:opacity-50';

function hasPaymentType(paymentTypes, value) {
  return paymentTypes.includes(value);
}

function normalizeStoredText(value) {
  return value == null ? '' : String(value).trim();
}

function normalizeStoredList(value) {
  return normalizeMultiValue(value)
    .map((item) => item.trim())
    .filter(Boolean)
    .sort();
}

function areListsEqual(first, second) {
  if (first.length !== second.length) return false;
  return first.every((value, index) => value === second[index]);
}

function isIndustryRequired(employmentStatus) {
  return INDUSTRY_REQUIRED_STATUSES.includes(employmentStatus);
}

function FinancialIndividual() {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const setFooterConfig = useOnboardingFooter();
  const { user, onboardingStatus, upsertingUserOnboardingProfile, upsertUserOnboardingProfileError } = useSelector((state) => state.user);
  const referenceCountries = useSelector((state) => state.config?.referenceCountries ?? []);
  const referenceCountriesLoading = useSelector((state) => state.config?.referenceCountriesLoading ?? false);
  const referenceCountriesError = useSelector((state) => state.config?.referenceCountriesError);
  const nextStepPath =
    getResolvedNextStepPath(location.pathname, onboardingStatus?.steps, { userType: user?.userType }) ?? '/compliance';
  const previousStepPath =
    getResolvedPreviousStepPath(location.pathname, onboardingStatus?.steps, { userType: user?.userType }) ?? '/collect-data';
  const existingProfile = useMemo(
    () => user?.userDetails?.onboardingProfile ?? user?.userDetails?.OnboardingProfile ?? {},
    [user?.userDetails?.OnboardingProfile, user?.userDetails?.onboardingProfile],
  );
  const firstName = user?.firstName?.trim() || 'there';
  const [screen, setScreen] = useState('usage');
  const [accountUse, setAccountUse] = useState('');
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [paymentTypesOther, setPaymentTypesOther] = useState('');
  const [monthlyPaymentVolume, setMonthlyPaymentVolume] = useState('');
  const [destinationCountries, setDestinationCountries] = useState([]);
  const [sourceIncome, setSourceIncome] = useState('');
  const [sourceIncomeOther, setSourceIncomeOther] = useState('');
  const [employmentStatus, setEmploymentStatus] = useState('');
  const [employmentStatusOther, setEmploymentStatusOther] = useState('');
  const [employmentIndustry, setEmploymentIndustry] = useState('');
  const [employmentIndustryOther, setEmploymentIndustryOther] = useState('');
  const [mostRecentOccupation, setMostRecentOccupation] = useState('');
  const [makingDecisionIndependently, setMakingDecisionIndependently] = useState(null);
  const [estimatedIncome, setEstimatedIncome] = useState('');
  const [errors, setErrors] = useState({});
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [pendingBackendProfile, setPendingBackendProfile] = useState(null);
  const [pendingBackendStep, setPendingBackendStep] = useState(null);
  const [hasSeenBackendSaving, setHasSeenBackendSaving] = useState(false);
  const [occupationOptions, setOccupationOptions] = useState([]);
  const [occupationsLoading, setOccupationsLoading] = useState(false);

  const isEnhancedKyc = useMemo(
    () => requiresEnhancedIndividualKyc(user, { extraCountryCodes: destinationCountries }),
    [destinationCountries, user],
  );
  const shouldShowIndustry = !isEnhancedKyc && isIndustryRequired(employmentStatus);
  const isKycOrLaterStep = isKycOrLaterOnboardingStep(onboardingStatus?.currentStep);

  const countryOptions = useMemo(() => normalizeReferenceCountries(referenceCountries), [referenceCountries]);

  useEffect(() => {
    if (countryOptions.length === 0 && !referenceCountriesLoading && !referenceCountriesError) {
      dispatch(configActions.getReferenceCountries());
    }
  }, [countryOptions.length, dispatch, referenceCountriesError, referenceCountriesLoading]);

  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      setOccupationsLoading(true);
      try {
        const data = await API.getOccupations();
        if (!isMounted) return;
        const normalized = Array.isArray(data)
          ? data
              .map((item) => {
                const code = item.code ?? item.Code ?? '';
                const description = item.description ?? item.Description ?? '';
                return {
                  value: code,
                  label: description,
                  searchText: `${description} ${code}`,
                };
              })
              .filter((option) => option.value && option.label)
          : [];
        setOccupationOptions(normalized);
      } catch (_) {
        if (isMounted) {
          setOccupationOptions([]);
        }
      } finally {
        if (isMounted) {
          setOccupationsLoading(false);
        }
      }
    };

    load();

    return () => {
      isMounted = false;
    };
  }, []);

  const availableCountryOptions = useMemo(
    () => countryOptions.filter((country) => !destinationCountries.includes(country.code)),
    [countryOptions, destinationCountries],
  );

  const selectedCountryLabels = useMemo(() => {
    const labels = new Map(countryOptions.map((country) => [country.code, country.name]));
    return destinationCountries.map((code) => ({
      code,
      label: labels.get(code) || code,
    }));
  }, [countryOptions, destinationCountries]);

  const sourceIncomeOptions = useMemo(
    () => SOURCE_INCOME_OPTIONS.map((option) => ({ value: option.value, label: option.label })),
    [],
  );

  const destinationCountrySelectOptions = useMemo(
    () =>
      availableCountryOptions.map((country) => ({
        value: country.code,
        label: country.name,
        searchText: `${country.name} ${country.code}`,
      })),
    [availableCountryOptions],
  );

  const validateUsageScreen = useCallback(() => {
    const nextErrors = {};

    if (!accountUse) nextErrors.accountUse = 'Select how you will mainly use your account.';
    if (paymentTypes.length === 0) nextErrors.paymentTypes = 'Select at least one payment type.';
    if (paymentTypes.includes('Other') && !paymentTypesOther.trim()) nextErrors.paymentTypesOther = 'Please describe the other payment type.';
    if (!monthlyPaymentVolume) nextErrors.monthlyPaymentVolume = 'Select the expected monthly payment volume.';
    if (destinationCountries.length === 0) nextErrors.destinationCountries = 'Select at least one destination country.';

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  }, [accountUse, destinationCountries.length, monthlyPaymentVolume, paymentTypes, paymentTypesOther]);

  const validateIncomeScreen = useCallback(() => {
    const nextErrors = {};

    if (!sourceIncome) nextErrors.sourceIncome = 'Select your main source of income.';
    if (!employmentStatus) nextErrors.employmentStatus = 'Select your work situation.';
    if (employmentStatus === 'Other' && !employmentStatusOther.trim()) nextErrors.employmentStatusOther = 'Please describe your work situation.';
    if (isEnhancedKyc) {
      if (!mostRecentOccupation) nextErrors.mostRecentOccupation = 'Select your most recent occupation.';
      if (typeof makingDecisionIndependently !== 'boolean') {
        nextErrors.makingDecisionIndependently = 'Please answer this question.';
      }
    } else {
      if (isIndustryRequired(employmentStatus) && !employmentIndustry) nextErrors.employmentIndustry = 'Select your industry.';
      if (employmentIndustry === 'Other' && !employmentIndustryOther.trim()) nextErrors.employmentIndustryOther = 'Please specify your industry.';
    }
    if (!estimatedIncome) nextErrors.estimatedIncome = 'Select your approximate annual income.';

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  }, [
    employmentIndustry,
    employmentIndustryOther,
    employmentStatus,
    employmentStatusOther,
    estimatedIncome,
    isEnhancedKyc,
    makingDecisionIndependently,
    mostRecentOccupation,
    sourceIncome,
  ]);

  useEffect(() => {
    const profile = existingProfile;

    setAccountUse(profile.accountUse ?? profile.AccountUse ?? '');
    setPaymentTypes(normalizeMultiValue(profile.paymentTypes ?? profile.PaymentTypes));
    setPaymentTypesOther(profile.paymentTypesOther ?? profile.PaymentTypesOther ?? '');
    setMonthlyPaymentVolume(profile.monthlyPaymentVolume ?? profile.MonthlyPaymentVolume ?? '');
    setDestinationCountries(normalizeMultiValue(profile.destinationCountries ?? profile.DestinationCountries).map((code) => code.toUpperCase()));
    setSourceIncome(profile.sourceIncome ?? profile.SourceIncome ?? '');
    setSourceIncomeOther(profile.sourceIncomeOther ?? profile.SourceIncomeOther ?? '');
    setEmploymentStatus(profile.employmentStatus ?? profile.EmploymentStatus ?? '');
    setEmploymentStatusOther(profile.employmentStatusOther ?? profile.EmploymentStatusOther ?? '');
    setEmploymentIndustry(profile.employmentIndustry ?? profile.EmploymentIndustry ?? '');
    setEmploymentIndustryOther(profile.employmentIndustryOther ?? profile.EmploymentIndustryOther ?? '');
    setMostRecentOccupation(profile.mostRecentOccupation ?? profile.MostRecentOccupation ?? '');
    const actingAsIntermediary = profile.actingAsIntermediary ?? profile.ActingAsIntermediary;
    setMakingDecisionIndependently(typeof actingAsIntermediary === 'boolean' ? !actingAsIntermediary : null);
    setEstimatedIncome(profile.estimatedIncome ?? profile.EstimatedIncome ?? '');
    setScreen(isFinancialFirstScreenComplete(user) && !isFinancialSecondScreenComplete(user) ? 'income' : 'usage');
  }, [existingProfile, user]);

  useEffect(() => {
    scrollOnboardingToTop();
  }, [screen]);

  useEffect(() => {
    if (employmentStatus && !shouldShowIndustry) {
      setEmploymentIndustry('');
      setEmploymentIndustryOther('');
    }
  }, [employmentStatus, shouldShowIndustry]);

  useEffect(() => {
    if (hasSubmitted) {
      if (screen === 'usage') {
        validateUsageScreen();
      } else {
        validateIncomeScreen();
      }
    }
  }, [
    accountUse,
    destinationCountries,
    employmentIndustry,
    employmentIndustryOther,
    employmentStatus,
    employmentStatusOther,
    estimatedIncome,
    hasSubmitted,
    monthlyPaymentVolume,
    paymentTypes,
    paymentTypesOther,
    screen,
    sourceIncome,
    validateIncomeScreen,
    validateUsageScreen,
  ]);

  const handlePaymentTypeToggle = (value) => {
    setPaymentTypes((currentPaymentTypes) => {
      if (currentPaymentTypes.includes(value)) {
        return currentPaymentTypes.filter((paymentType) => paymentType !== value);
      }
      return [...currentPaymentTypes, value];
    });
  };

  const handleCountrySelect = (countryCode) => {
    if (!countryCode) return;
    setDestinationCountries((currentCountries) => (currentCountries.includes(countryCode) ? currentCountries : [...currentCountries, countryCode]));
  };

  const handleCountryRemove = (countryCode) => {
    setDestinationCountries((currentCountries) => currentCountries.filter((code) => code !== countryCode));
  };

  const getUsageProfilePatch = useCallback(
    () => ({
      accountUse,
      paymentTypes: paymentTypes.join(','),
      paymentTypesOther: hasPaymentType(paymentTypes, 'Other') ? paymentTypesOther.trim() : null,
      monthlyPaymentVolume,
      destinationCountries: destinationCountries.join(','),
    }),
    [accountUse, destinationCountries, monthlyPaymentVolume, paymentTypes, paymentTypesOther],
  );

  const saveUsageScreen = useCallback(() => {
    const profile = buildUserOnboardingProfilePayload({
      ...existingProfile,
      ...getUsageProfilePatch(),
    });

    return {
      ...profile,
      financialFirstScreenCompleted: true,
    };
  }, [existingProfile, getUsageProfilePatch]);

  const isUsageScreenUnchanged = useCallback(() => {
    const profilePatch = getUsageProfilePatch();

    return (
      isFinancialFirstScreenComplete(user) &&
      normalizeStoredText(existingProfile.accountUse ?? existingProfile.AccountUse) === normalizeStoredText(profilePatch.accountUse) &&
      areListsEqual(normalizeStoredList(existingProfile.paymentTypes ?? existingProfile.PaymentTypes), normalizeStoredList(profilePatch.paymentTypes)) &&
      normalizeStoredText(existingProfile.paymentTypesOther ?? existingProfile.PaymentTypesOther) === normalizeStoredText(profilePatch.paymentTypesOther) &&
      normalizeStoredText(existingProfile.monthlyPaymentVolume ?? existingProfile.MonthlyPaymentVolume) === normalizeStoredText(profilePatch.monthlyPaymentVolume) &&
      areListsEqual(
        normalizeStoredList(existingProfile.destinationCountries ?? existingProfile.DestinationCountries),
        normalizeStoredList(profilePatch.destinationCountries),
      )
    );
  }, [existingProfile, getUsageProfilePatch, user]);

  const saveIncomeScreen = useCallback(() => {
    let nextEmploymentIndustry = null;
    let nextEmploymentIndustryOther = null;
    if (!isEnhancedKyc && shouldShowIndustry) {
      nextEmploymentIndustry = employmentIndustry;
      if (employmentIndustry === 'Other') {
        nextEmploymentIndustryOther = employmentIndustryOther.trim();
      }
    }

    const profilePatch = {
      sourceIncome,
      sourceIncomeOther: sourceIncome === 'Other' ? sourceIncomeOther.trim() || null : null,
      employmentStatus,
      employmentStatusOther: employmentStatus === 'Other' ? employmentStatusOther.trim() : null,
      employmentIndustry: nextEmploymentIndustry,
      employmentIndustryOther: nextEmploymentIndustryOther,
      mostRecentOccupation: isEnhancedKyc ? mostRecentOccupation : null,
      actingAsIntermediary: isEnhancedKyc && typeof makingDecisionIndependently === 'boolean' ? !makingDecisionIndependently : null,
      estimatedIncome,
    };

    const profile = buildUserOnboardingProfilePayload({
      ...existingProfile,
      ...getUsageProfilePatch(),
      ...profilePatch,
    });

    return {
      ...profile,
      financialSecondScreenCompleted: true,
    };
  }, [
    employmentIndustry,
    employmentIndustryOther,
    employmentStatus,
    employmentStatusOther,
    estimatedIncome,
    existingProfile,
    getUsageProfilePatch,
    isEnhancedKyc,
    makingDecisionIndependently,
    mostRecentOccupation,
    shouldShowIndustry,
    sourceIncome,
    sourceIncomeOther,
  ]);

  const handleContinue = useCallback(() => {
    if (isKycOrLaterStep) {
      if (nextStepPath) navigate(nextStepPath);
      return;
    }

    setHasSubmitted(true);

    if (screen === 'usage') {
      if (!validateUsageScreen()) return;
      if (isUsageScreenUnchanged()) {
        setErrors({});
        setHasSubmitted(false);
        setScreen('income');
        return;
      }
      const profile = saveUsageScreen();
      setPendingBackendProfile(profile);
      setPendingBackendStep('usage');
      setHasSeenBackendSaving(false);
      dispatch(userActions.upsertUserOnboardingProfile(profile));
      return;
    }

    if (!validateIncomeScreen()) return;
    const profile = saveIncomeScreen();
    setPendingBackendProfile(profile);
    setPendingBackendStep('income');
    setHasSeenBackendSaving(false);
    dispatch(userActions.upsertUserOnboardingProfile(profile));
  }, [
    dispatch,
    isKycOrLaterStep,
    isUsageScreenUnchanged,
    navigate,
    nextStepPath,
    saveIncomeScreen,
    saveUsageScreen,
    screen,
    validateIncomeScreen,
    validateUsageScreen,
  ]);

  const handleBack = useCallback(() => {
    if (screen === 'income') {
      setErrors({});
      setHasSubmitted(false);
      setScreen('usage');
      return;
    }

    if (previousStepPath) {
      navigate(previousStepPath);
    }
  }, [navigate, previousStepPath, screen]);

  useEffect(() => {
    setFooterConfig({
      continueLabel: upsertingUserOnboardingProfile ? 'Saving...' : 'Continue',
      continueDisabled: upsertingUserOnboardingProfile,
      onContinue: handleContinue,
      backDisabled: upsertingUserOnboardingProfile || (screen === 'usage' && !previousStepPath),
      onBack: handleBack,
    });
  }, [handleBack, handleContinue, previousStepPath, screen, setFooterConfig, upsertingUserOnboardingProfile]);

  useEffect(() => () => setFooterConfig({}), [setFooterConfig]);

  useEffect(() => {
    if (pendingBackendProfile && upsertingUserOnboardingProfile) {
      setHasSeenBackendSaving(true);
    }
  }, [pendingBackendProfile, upsertingUserOnboardingProfile]);

  useEffect(() => {
    if (pendingBackendProfile && hasSeenBackendSaving && !upsertingUserOnboardingProfile && !upsertUserOnboardingProfileError) {
      setPendingBackendProfile(null);
      setPendingBackendStep(null);
      setHasSeenBackendSaving(false);
      if (pendingBackendStep === 'usage') {
        setErrors({});
        setHasSubmitted(false);
        setScreen('income');
        return;
      }
      if (nextStepPath) navigate(nextStepPath);
    }
  }, [
    hasSeenBackendSaving,
    navigate,
    nextStepPath,
    pendingBackendProfile,
    pendingBackendStep,
    upsertUserOnboardingProfileError,
    upsertingUserOnboardingProfile,
  ]);

  const renderUsageScreen = () => (
    <>
      <div className='mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-[#EBF4FB] text-[#1E4E80]'>
        <CreditCardIcon className='h-6 w-6' />
      </div>

      <h1 className='mb-2.5 font-serif text-2xl font-normal leading-[1.25] text-[#0B1929]'>How you&apos;ll use HansePay, {firstName}.</h1>

      <fieldset className='mt-5'>
        <legend className={fieldLabelClass}>What will you mainly use your account for?</legend>
        <div className='space-y-3'>
          {ACCOUNT_USE_OPTIONS.map(({ value, title, description, Icon }) => {
            const isSelected = accountUse === value;
            return (
              <button
                key={value}
                type='button'
                onClick={() => setAccountUse(value)}
                className={`flex w-full items-center gap-3 rounded-[12px] border bg-white p-3 text-left transition ${
                  isSelected ? 'border-[#1E4E80] shadow-[0_0_0_3px_rgba(30,78,128,0.08)]' : 'border-[#DDE6EF] hover:border-[#B8C9D9]'
                }`}
              >
                <span className='flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-[10px] bg-[#F4F8FC] text-[#4E6F8A]'>
                  <Icon className='h-5 w-5' />
                </span>
                <span>
                  <span className='block text-sm font-normal text-[#0B1929]'>{title}</span>
                  <span className='mt-0.5 block text-xs text-[#4E6F8A]'>{description}</span>
                </span>
              </button>
            );
          })}
        </div>
        {errors.accountUse && <p className={errorClass}>{errors.accountUse}</p>}
      </fieldset>

      <fieldset className='mt-6'>
        <legend className={fieldLabelClass}>What types of payments do you expect to make?</legend>
        <div className='grid grid-cols-1 gap-2 sm:grid-cols-2'>
          {PAYMENT_TYPE_OPTIONS.map(({ value, label }) => {
            const isSelected = hasPaymentType(paymentTypes, value);
            return (
              <button
                key={value}
                type='button'
                onClick={() => handlePaymentTypeToggle(value)}
                className={`min-h-[38px] rounded-full border px-4 py-2 text-left text-[12.5px] font-normal transition ${
                  isSelected
                    ? 'border-[#1E4E80] bg-[#EBF4FB] text-[#0B1929]'
                    : 'border-[#DDE6EF] bg-white text-[#3D5A73] hover:border-[#B8C9D9]'
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>
        {hasPaymentType(paymentTypes, 'Other') && (
          <div className='mt-3'>
            <label htmlFor='paymentTypesOther' className={fieldLabelClass}>
              Please describe
            </label>
            <input
              id='paymentTypesOther'
              type='text'
              value={paymentTypesOther}
              onChange={(event) => setPaymentTypesOther(event.target.value)}
              placeholder='e.g. import payments, currency conversion...'
              className={inputClass}
            />
            {errors.paymentTypesOther && <p className={errorClass}>{errors.paymentTypesOther}</p>}
          </div>
        )}
        {errors.paymentTypes && <p className={errorClass}>{errors.paymentTypes}</p>}
      </fieldset>

      <fieldset className='mt-6'>
        <legend className={fieldLabelClass}>Expected monthly payment volume</legend>
        <div className='grid grid-cols-1 gap-2 sm:grid-cols-2'>
          {MONTHLY_VOLUME_OPTIONS.map(({ value, label }) => {
            const isSelected = monthlyPaymentVolume === value;
            return (
              <button
                key={value}
                type='button'
                onClick={() => setMonthlyPaymentVolume(value)}
                className={`rounded-[12px] border bg-white px-4 py-3 text-left text-sm font-normal transition ${
                  isSelected ? 'border-[#1E4E80] shadow-[0_0_0_3px_rgba(30,78,128,0.08)]' : 'border-[#DDE6EF] hover:border-[#B8C9D9]'
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>
        {errors.monthlyPaymentVolume && <p className={errorClass}>{errors.monthlyPaymentVolume}</p>}
      </fieldset>
    </>
  );

  const renderDestinationCountries = () => (
    <div className={`mt-6 ${isKycOrLaterStep ? 'pointer-events-none opacity-60' : ''}`}>
      <label htmlFor='destinationCountrySelect' className={fieldLabelClass}>
        Which countries will you usually send payments to?
      </label>
      <SearchableSelect
        id='destinationCountrySelect'
        name='destinationCountrySelect'
        value=''
        onChange={(event) => handleCountrySelect(event.target.value)}
        placeholder={referenceCountriesLoading ? 'Loading countries...' : 'Select country...'}
        options={destinationCountrySelectOptions}
        showCountryFlags
        searchPlaceholder='Search country...'
        menuPlacement='auto'
        menuMaxHeight={240}
        menuMinWidth='w-full'
        hasError={Boolean(errors.destinationCountries)}
        triggerClassName={searchableSelectTriggerClass}
      />
      <p className='mt-2 text-xs leading-5 text-[#7A9AB0]'>Select each country you expect to send payments to. You can add more than one.</p>
      {referenceCountriesError && (
        <p className={`${errorClass} mt-2`}>Could not load countries. Please refresh and try again.</p>
      )}

      {selectedCountryLabels.length > 0 && (
        <div className='mt-3 flex flex-wrap gap-2'>
          {selectedCountryLabels.map((country) => (
            <button
              key={country.code}
              type='button'
              onClick={() => handleCountryRemove(country.code)}
              className='inline-flex items-center gap-1.5 rounded-full border border-[#DDE6EF] bg-[#F8FBFE] px-3 py-1.5 text-xs font-normal text-[#0B1929] transition hover:border-[#B8C9D9]'
              title='Remove country'
            >
              <FlagIcon countryCode={country.code} className='h-3.5 w-[18px] rounded-[2px]' />
              {country.label} <span className='ml-1 text-[#7A9AB0]'>x</span>
            </button>
          ))}
        </div>
      )}
      {errors.destinationCountries && <p className={errorClass}>{errors.destinationCountries}</p>}
    </div>
  );

  const renderIncomeScreen = () => (
    <>
      <div className='mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-[#EBF4FB] text-[#1E4E80]'>
        <BriefcaseIcon className='h-6 w-6' />
      </div>

      <h1 className='mb-2.5 font-serif text-2xl font-normal leading-[1.25] text-[#0B1929]'>Your income &amp; work.</h1>
      <p className='mt-2 text-sm leading-6 text-[#4E6F8A]'>A few quick questions to set up your account correctly.</p>

      <div className='mt-5'>
        <label htmlFor='sourceIncome' className={fieldLabelClass}>
          Main source of income
        </label>
        <SearchableSelect
          id='sourceIncome'
          name='sourceIncome'
          value={sourceIncome}
          onChange={(event) => {
            const { value } = event.target;
            setSourceIncome(value);
            if (value !== 'Other') setSourceIncomeOther('');
          }}
          placeholder='Select...'
          options={sourceIncomeOptions}
          searchPlaceholder='Search income source'
          menuPlacement='auto'
          menuMinWidth='w-full'
          clearable
          hasError={Boolean(errors.sourceIncome)}
          triggerClassName={searchableSelectTriggerClass}
        />
        {errors.sourceIncome && <p className={errorClass}>{errors.sourceIncome}</p>}
      </div>

      {sourceIncome === 'Other' && (
        <div className='mt-4'>
          <label htmlFor='sourceIncomeOther' className={fieldLabelClass}>
            Please specify <span className='font-normal normal-case opacity-60'>(optional)</span>
          </label>
          <input id='sourceIncomeOther' type='text' value={sourceIncomeOther} onChange={(event) => setSourceIncomeOther(event.target.value)} className={inputClass} />
        </div>
      )}

      <fieldset className='mt-6'>
        <legend className={fieldLabelClass}>Work situation</legend>
        <div className='flex flex-wrap gap-2'>
          {EMPLOYMENT_STATUS_OPTIONS.map(({ value, label }) => {
            const isSelected = employmentStatus === value;
            return (
              <button
                key={value}
                type='button'
                onClick={() => setEmploymentStatus(value)}
                className={`rounded-full border px-4 py-2 text-[12.5px] font-normal transition ${
                  isSelected ? 'border-[#1E4E80] bg-[#EBF4FB] text-[#0B1929]' : 'border-[#DDE6EF] bg-white text-[#3D5A73] hover:border-[#B8C9D9]'
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>

        {employmentStatus === 'Other' && (
          <div className='mt-3'>
            <label htmlFor='employmentStatusOther' className={fieldLabelClass}>
              Please describe
            </label>
            <input
              id='employmentStatusOther'
              type='text'
              value={employmentStatusOther}
              onChange={(event) => setEmploymentStatusOther(event.target.value)}
              placeholder='e.g. freelancer, part-time'
              className={inputClass}
            />
            {errors.employmentStatusOther && <p className={errorClass}>{errors.employmentStatusOther}</p>}
          </div>
        )}
        {errors.employmentStatus && <p className={errorClass}>{errors.employmentStatus}</p>}
      </fieldset>

      {isEnhancedKyc ? (
        <>
          <div className='mt-6'>
            <label htmlFor='mostRecentOccupation' className={fieldLabelClass}>
              Most recent occupation
            </label>
            <SearchableSelect
              id='mostRecentOccupation'
              name='mostRecentOccupation'
              value={mostRecentOccupation}
              onChange={(event) => setMostRecentOccupation(event.target.value)}
              placeholder={occupationsLoading ? 'Loading occupations…' : 'Select occupation...'}
              options={occupationOptions}
              searchPlaceholder='Search occupation'
              menuPlacement='auto'
              menuMinWidth='w-full'
              clearable
              hasError={Boolean(errors.mostRecentOccupation)}
              triggerClassName={searchableSelectTriggerClass}
            />
            {errors.mostRecentOccupation && <p className={errorClass}>{errors.mostRecentOccupation}</p>}
          </div>

          <fieldset className='mt-6'>
            <legend className={fieldLabelClass}>
              Are you making this decision on your own, without an intermediary acting for you?
            </legend>
            <div className='mt-2 grid grid-cols-2 gap-2'>
              {[
                { value: true, label: 'Yes' },
                { value: false, label: 'No' },
              ].map((option) => {
                const isSelected = makingDecisionIndependently === option.value;
                return (
                  <button
                    key={option.label}
                    type='button'
                    onClick={() => setMakingDecisionIndependently(option.value)}
                    className={`rounded-[12px] border bg-white px-4 py-3 text-left text-sm font-normal transition ${
                      isSelected ? 'border-[#1E4E80] shadow-[0_0_0_3px_rgba(30,78,128,0.08)]' : 'border-[#DDE6EF] hover:border-[#B8C9D9]'
                    }`}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
            {errors.makingDecisionIndependently && <p className={errorClass}>{errors.makingDecisionIndependently}</p>}
          </fieldset>
        </>
      ) : (
        shouldShowIndustry && (
        <fieldset className='mt-6'>
          <legend className={fieldLabelClass}>Industry</legend>
          <div className='grid grid-cols-2 gap-2 sm:grid-cols-4'>
            {INDUSTRY_OPTIONS.map(({ value, label, icon }) => {
              const isSelected = employmentIndustry === value;
              return (
                <button
                  key={value}
                  type='button'
                  onClick={() => setEmploymentIndustry(value)}
                  className={`flex min-h-[78px] flex-col items-center justify-center gap-1.5 rounded-[12px] border px-2 py-2 text-center transition ${
                    isSelected ? 'border-[#1E4E80] bg-[#EBF4FB] text-[#0B1929]' : 'border-[#DDE6EF] bg-white text-[#3D5A73] hover:border-[#B8C9D9]'
                  }`}
                >
                  <IndustryIcon variant={icon} className='h-[18px] w-[18px]' />
                  <span className='text-[10.5px] font-normal leading-tight'>{label}</span>
                </button>
              );
            })}
          </div>

          {employmentIndustry === 'Other' && (
            <div className='mt-3'>
              <label htmlFor='employmentIndustryOther' className={fieldLabelClass}>
                Please specify your industry
              </label>
              <input
                id='employmentIndustryOther'
                type='text'
                value={employmentIndustryOther}
                onChange={(event) => setEmploymentIndustryOther(event.target.value)}
                className={inputClass}
              />
              {errors.employmentIndustryOther && <p className={errorClass}>{errors.employmentIndustryOther}</p>}
            </div>
          )}
          {errors.employmentIndustry && <p className={errorClass}>{errors.employmentIndustry}</p>}
        </fieldset>
        )
      )}

      <fieldset className='mt-6'>
        <legend className={fieldLabelClass}>Approximate annual income before tax</legend>
        <div className='grid grid-cols-1 gap-2 sm:grid-cols-2'>
          {ESTIMATED_INCOME_OPTIONS.map(({ value, label }) => {
            const isSelected = estimatedIncome === value;
            return (
              <button
                key={value}
                type='button'
                onClick={() => setEstimatedIncome(value)}
                className={`rounded-[12px] border bg-white px-4 py-3 text-left text-sm font-normal transition ${
                  isSelected ? 'border-[#1E4E80] shadow-[0_0_0_3px_rgba(30,78,128,0.08)]' : 'border-[#DDE6EF] hover:border-[#B8C9D9]'
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>
        {errors.estimatedIncome && <p className={errorClass}>{errors.estimatedIncome}</p>}
      </fieldset>
    </>
  );

  return (
    <div className='flex w-full justify-center bg-[#EBF4FB] px-4  pt-7 font-sans text-[#0B1929] sm:min-h-screen sm:px-6'>
      <div className='w-full max-w-[540px]'>
        <div className='overflow-visible rounded-[20px] bg-white px-6 py-8 shadow-[0_4px_32px_rgba(11,25,41,0.08)] sm:mb-24 sm:px-10 sm:py-9'>
          <fieldset disabled={isKycOrLaterStep} className='block min-w-0 border-0 p-0'>
            {screen === 'usage' ? renderUsageScreen() : renderIncomeScreen()}
          </fieldset>
          {screen === 'usage' && renderDestinationCountries()}

          {upsertUserOnboardingProfileError && (
            <div className='mt-5 rounded-[12px] border border-[#F4C7C3] bg-[#FFF7F6] px-4 py-3 text-sm font-medium text-[#A83227]'>
              {upsertUserOnboardingProfileError?.message ??
                upsertUserOnboardingProfileError?.detail ??
                'Could not save your financial profile. Please try again.'}
            </div>
          )}

          {Object.keys(errors).length > 0 && hasSubmitted && (
            <div className='mt-5 rounded-[12px] border border-[#F4C7C3] bg-[#FFF7F6] px-4 py-3 text-sm font-medium text-[#A83227]'>
              Please complete the highlighted fields before continuing.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Financial() {
  const { user } = useSelector((state) => state.user);
  if (!user) return null;
  if (user.userType === UserType.Company) return <CompanyActivity />;
  return <FinancialIndividual />;
}

export default Financial;
